/**
 * PLAN-CREDIT-14 (Seth 2026-10-28, "GO with those"): the recent-visit credit.
 *
 * A customer who paid a visit in full and then says yes to a plan within 14 days
 * gets the member savings that invoice WOULD have earned — the member diagnostic
 * difference ($109 → $79) plus the repair % for the plan's term on repair parts
 * and labor — taken OFF THE PLAN PRICE. Never cash, never a reopened invoice,
 * capped at the plan price. Office-only: the 360 Membership tab and the Plan
 * Builder show it; the homeowner page never advertises it, so nobody learns
 * to wait. The number comes from `pegasus_plan_visit_credit` (ESC invoices and
 * Pegasus invoices alike); the office can still change it before selling.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface PlanVisitCredit {
  source: "esc" | "pegasus";
  invoice: string;
  date: string;          // ISO date of the visit / invoice
  total: number;         // what they paid
  term: number;
  pct: number;           // the repair % used (fraction)
  diag_savings: number;
  repair_savings: number;
  credit: number;        // diag + repair savings, before the plan-price cap
  until: string;         // ISO date the credit is good through
  window_days: number;
}

export function usePlanVisitCredit(custno: string | null | undefined, term: number) {
  return useQuery({
    queryKey: ["plan_visit_credit", custno, term],
    enabled: !!custno,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<PlanVisitCredit | null> => {
      const { data, error } = await supabase.rpc("pegasus_plan_visit_credit", { p_custno: custno, p_term: term });
      if (error) throw error;
      const c = data as PlanVisitCredit | null;
      return c && Number(c.credit) > 0 ? { ...c, total: Number(c.total), pct: Number(c.pct), diag_savings: Number(c.diag_savings), repair_savings: Number(c.repair_savings), credit: Number(c.credit) } : null;
    },
  });
}

export const fmtShort = (iso: string) => new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
